import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent, CartUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    this.#getInitialQuantity();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#abortController.abort();
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (!footer) return;

    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      if (!entry.isIntersecting && !this.#isStuck) {
        const rect = entry.target.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top < 0) {
          this.#showStickyBar();
        }
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hiddenByBottom = false;
        this.#hideStickyBar();
      }
    });

    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          const rect = buyButtonsBlock.getBoundingClientRect();
          if (rect.bottom < 0 || rect.top < 0) {
            this.#hiddenByBottom = false;
            this.#showStickyBar();
          }
        }
      },
      {
        rootMargin: '200px 0px 0px 0px',
      }
    );

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#mainBottomObserver.observe(footer);
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
  }

  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = async (event) => {
    event?.preventDefault();

    const productId = this.dataset.productId;
    const variantId = this.dataset.currentVariantId;
    const quantity = this.#currentQuantity || 1;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    // If product has variants, show bottom sheet modal (unless called from modal confirm)
    if (!this.dataset.skipModal) {
      const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
      if (variantPicker) {
        StickyVariantModal.open(variantPicker, this);
        return;
      }
    }
    delete this.dataset.skipModal;

    if (!variantId) return;

    try {
      this.refs.addToCartButton.setAttribute('disabled', 'disabled');

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          id: Number(variantId),
          quantity,
          sections: Array.from(
            new Set(
              Array.from(document.querySelectorAll('[data-section-id]'))
                .map(el => el.dataset.sectionId)
                .filter(Boolean)
            )
          ).join(','),
          sections_url: window.location.pathname,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        document.dispatchEvent(
          new CustomEvent(ThemeEvents.cartError, {
            bubbles: true,
            detail: { error: data },
          })
        );
        throw new Error(data?.description || 'Erro ao adicionar ao carrinho');
      }

      const itemCount = data.items?.reduce((sum, item) => sum + item.quantity, 0) ?? 0;

      const cartUpdateEvent = new CartUpdateEvent(data, this.id || 'sticky-add-to-cart', {
        source: 'sticky-add-to-cart',
        itemCount,
        sections: data.sections || {},
      });
      document.dispatchEvent(cartUpdateEvent);

      document.querySelector('cart-drawer-component')?.open?.();

      if (this.refs.addToCartButton.dataset.added !== 'true') {
        this.refs.addToCartButton.dataset.added = 'true';
      }

      if (cartIcon && this.refs.addToCartButton && this.refs.productImage) {
        if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

        const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));
        flyToCartElement.classList.add('fly-to-cart--sticky');
        flyToCartElement.style.setProperty('background-image', `url(${this.refs.productImage.src})`);
        flyToCartElement.useSourceSize = 'true';
        flyToCartElement.source = this.refs.productImage;
        flyToCartElement.destination = cartIcon;

        document.body.appendChild(flyToCartElement);

        await onAnimationEnd([this.refs.addToCartButton, flyToCartElement]);

        this.#resetTimeout = setTimeout(() => {
          this.refs.addToCartButton.removeAttribute('data-added');
        }, 800);
      }
    } catch (error) {
      console.error(error);
    } finally {
      this.refs.addToCartButton.removeAttribute('disabled');
    }
  };

  /**
   * Handles variant update events
   * @param {CustomEvent} event - The variant update event
   */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const variant = event.detail.resource;

    const newStickyAddToCart = event.detail.data.html.querySelector('sticky-add-to-cart');
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
    const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

    this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
    this.dataset.variantAvailable = variantAvailable;

    if (variant && variant.id) {
      this.dataset.currentVariantId = variant.id;
    }

    const productForm = this.#getProductForm();
    if (productForm) {
      this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    }

    if (variant == null) {
      this.#handleVariantUnavailable();
    }

    this.#updateButtonText();
  };

  /**
   * Handles variant selected events
   * @param {CustomEvent} event - The variant selected event
   */
  #handleVariantSelected = (event) => {
    const variantId = event.detail.resource?.id;
    if (!variantId) return;
    this.dataset.currentVariantId = variantId;
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete (success or error)
   * @param {CustomEvent} _event - The cart event (unused)
   */
  #handleCartAddComplete = (_event) => {
    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = 'false';
    }
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
  }

  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const available = !addToCartButton.disabled;

    quantityNumber.textContent = this.#currentQuantity.toString();

    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}

// ── STICKY VARIANT MODAL ──────────────────────────────────────────────────────
class StickyVariantModal {
  static #overlay = null;
  static #sheet = null;
  static #stickyComponent = null;

  static open(variantPicker, stickyComponent) {
    this.#stickyComponent = stickyComponent;
    this.#build(variantPicker);
  }

  static #build(variantPicker) {
    // Remove existing
    this.#overlay?.remove();

    const productTitle = variantPicker.closest('.shopify-section')?.querySelector('.product__title, h1, [data-testid="product-title"]')?.textContent?.trim() || '';

    // Overlay
    const overlay = document.createElement('div');
    overlay.className = 'svm-overlay';
    overlay.addEventListener('click', () => this.close());

    // Sheet
    const sheet = document.createElement('div');
    sheet.className = 'svm-sheet';
    sheet.setAttribute('role', 'dialog');
    sheet.setAttribute('aria-modal', 'true');
    sheet.addEventListener('click', (e) => {
      // Handle size chart button inside the sheet
      const sizeBtn = e.target.closest('[data-dialog-id]');
      if (sizeBtn) {
        e.stopPropagation();
        const dialog = document.getElementById(sizeBtn.dataset.dialogId);
        if (dialog) dialog.showModal();
        return;
      }
      e.stopPropagation();
    });

    // Header
    const header = document.createElement('div');
    header.className = 'svm-header';
    header.innerHTML = `
      <div class="svm-handle"></div>
      <span class="svm-title">Select options</span>
      <button class="svm-close" aria-label="Close">×</button>
    `;
    header.querySelector('.svm-close').addEventListener('click', () => this.close());

    // Clone variant picker options
    const optionsWrap = document.createElement('div');
    optionsWrap.className = 'svm-options';

    const fieldsets = variantPicker.querySelectorAll('.variant-option');
    fieldsets.forEach(fieldset => {
      const clone = fieldset.cloneNode(true);
      // Fix input names to be unique in modal
      clone.querySelectorAll('input[type="radio"]').forEach(input => {
        input.name = 'svm-' + input.name;
        if (input.checked || input.dataset.currentChecked === 'true') {
          input.checked = true;
        }
        input.addEventListener('change', () => this.#updateSelection(variantPicker));
      });
      optionsWrap.appendChild(clone);
    });

    // CTA button
    const cta = document.createElement('button');
    cta.className = 'svm-cta';
    cta.textContent = 'Add to cart';
    cta.addEventListener('click', () => this.#confirm(variantPicker));

    sheet.appendChild(header);
    sheet.appendChild(optionsWrap);
    sheet.appendChild(cta);
    overlay.appendChild(sheet);
    document.body.appendChild(overlay);

    this.#overlay = overlay;
    this.#sheet = sheet;

    // Trigger open animation
    requestAnimationFrame(() => {
      overlay.classList.add('svm-overlay--open');
      sheet.classList.add('svm-sheet--open');
    });

    // Escape key
    this._keyHandler = (e) => { if (e.key === 'Escape') this.close(); };
    document.addEventListener('keydown', this._keyHandler);
  }

  static #updateSelection(variantPicker) {
    // Sync selected variant back to sticky component
    const sheet = this.#sheet;
    if (!sheet) return;

    const selectedValues = [];
    sheet.querySelectorAll('.variant-option').forEach(fieldset => {
      const checked = fieldset.querySelector('input:checked');
      if (checked) selectedValues.push(checked.value);
    });

    // Find matching variant in the page's variant picker
    const allInputs = variantPicker.querySelectorAll('input[type="radio"]');
    const variants = [];
    try {
      const json = variantPicker.querySelector('script[type="application/json"]');
      // We match by checking which variant has all selected options
    } catch(e) {}
  }

  static async #confirm(variantPicker) {
    const sheet = this.#sheet;
    if (!sheet) return;

    // Get selected values from modal
    const selectedValues = [];
    sheet.querySelectorAll('.variant-option').forEach(fieldset => {
      const checked = fieldset.querySelector('input:checked, select');
      if (checked) {
        selectedValues.push(checked.value);
      }
    });

    // Find matching variant ID from page variant picker
    let variantId = null;
    const allRadios = variantPicker.querySelectorAll('input[data-variant-id]');
    
    // For products with only one option
    if (selectedValues.length === 1) {
      const matching = variantPicker.querySelector(`input[value="${CSS.escape(selectedValues[0])}"][data-variant-id]`);
      if (matching) variantId = matching.dataset.variantId;
    } else {
      // Multi-option: find variant where all options match
      // Use the variant picker's JSON data
      const jsonScript = variantPicker.querySelector('script[type="application/json"]');
      if (jsonScript) {
        // Fetch all variants for this product
        try {
          const productUrl = variantPicker.dataset.productUrl;
          const res = await fetch(`${productUrl}.js`);
          const product = await res.json();
          const match = product.variants.find(v => 
            v.options.every((opt, i) => opt === selectedValues[i])
          );
          if (match) variantId = String(match.id);
        } catch(e) {}
      }
    }

    if (!variantId) {
      // Fallback: use sticky's current variant
      variantId = this.#stickyComponent?.dataset?.currentVariantId;
    }

    if (!variantId) return;

    // Set variant and trigger add to cart
    this.#stickyComponent.dataset.currentVariantId = variantId;
    this.#stickyComponent.dataset.skipModal = 'true';
    this.close();
    setTimeout(() => {
      this.#stickyComponent.handleAddToCartClick();
    }, 50);
  }

  static close() {
    if (!this.#overlay) return;
    this.#overlay.classList.remove('svm-overlay--open');
    this.#sheet?.classList.remove('svm-sheet--open');
    document.removeEventListener('keydown', this._keyHandler);
    setTimeout(() => {
      this.#overlay?.remove();
      this.#overlay = null;
      this.#sheet = null;
    }, 320);
  }
}

// Inject modal styles
const svmStyles = document.createElement('style');
svmStyles.textContent = `
  .svm-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0,0,0,0);
    z-index: 9999;
    transition: background 0.3s ease;
  }
  .svm-overlay--open {
    background: rgba(0,0,0,0.45);
  }
  .svm-sheet {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    background: #fff;
    border-radius: 0;
    padding: 0 0 env(safe-area-inset-bottom, 0);
    transform: translateY(100%);
    transition: transform 0.32s cubic-bezier(0.32, 0.72, 0, 1);
    max-height: 90vh;
    overflow-y: auto;
  }
  .svm-sheet--open {
    transform: translateY(0);
  }
  .svm-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px 12px;
    position: relative;
  }
  .svm-handle {
    position: absolute;
    top: 8px;
    left: 50%;
    transform: translateX(-50%);
    width: 36px;
    height: 4px;
    background: #ddd;
    border-radius: 2px;
  }
  .svm-title {
    font-size: 15px;
    font-weight: 700;
    letter-spacing: .02em;
  }
  .svm-close {
    background: none;
    border: none;
    font-size: 24px;
    cursor: pointer;
    line-height: 1;
    color: #666;
    padding: 0;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .svm-options {
    padding: 4px 20px 16px;
  }
  .svm-options .variant-option {
    border: none;
    padding: 0;
    margin: 0 0 16px;
  }
  .svm-options legend {
    font-size: 13px;
    font-weight: 600;
    margin-bottom: 8px;
    padding: 0;
  }
  .svm-options .variant-option--buttons {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }
  .svm-cta {
    display: block;
    width: calc(100% - 40px);
    margin: 0 20px 24px;
    padding: 16px;
    background: #1a1a1a;
    color: #fff;
    border: none;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    letter-spacing: .04em;
  }
  .svm-cta:hover {
    background: #333;
  }
`;
document.head.appendChild(svmStyles);